
import Article from "@/components/Article";
import { ArticleData } from "@/data/articles";
import { useEffect, useState } from "react";
import { isProductionBuild, isViteAssetReference, shouldBypassContentProcessing, cleanupHomePageContent } from "@/utils/productionFallbacks";

interface ArticleRendererProps {
  article: ArticleData;
  previousArticleLink?: string;
  previousArticleTitle?: string;
  isPremium: boolean;
  isPreviewMode: boolean;
}

const ArticleRenderer = ({
  article,
  previousArticleLink,
  previousArticleTitle,
  isPremium,
  isPreviewMode
}: ArticleRendererProps) => {
  const isProduction = isProductionBuild();
  const isHomepage = shouldBypassContentProcessing();
  // Add a loading state to manage content initialization
  const [isInitializing, setIsInitializing] = useState(true);
  
  useEffect(() => {
    console.log("ArticleRenderer - Mounted with article:", {
      title: article.title,
      slug: article.slug,
      isPremium,
      isPreviewMode,
      isHomepage,
      hasContent: !!article.content,
      contentType: article.contentType || "Not specified",
      contentPreview: article.content ? article.content.substring(0, 20) + "..." : "undefined...",
      contentPath: article.contentPath || "No path specified",
      contentLength: article.content?.length || 0
    });
    
    // Special logging for homepage
    if (isHomepage) {
      console.log("ArticleRenderer - Homepage detected:", {
        url: window.location.href,
        pathname: window.location.pathname,
        articleTitle: article.title,
        articleSlug: article.slug
      });
    }
    
    // Add detailed logging for potential Vite reference detection
    if (article.content) {
      console.log("ArticleRenderer - Content format analysis:", {
        containsExportDefault: article.content.includes("export default"),
        containsAssetsPath: article.content.includes("/assets/"),
        startsWithTag: article.content.trim().startsWith("<"),
        startsWithHash: article.content.trim().startsWith("#"),
        containsHtmlExtension: article.content.includes(".html"),
        contentFirstChars: JSON.stringify(article.content.substring(0, 100)),
        isViteReference: isViteAssetReference(article.content)
      });
    }
    
    // Set a short timeout to give content time to initialize
    // This prevents the flash of error message
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 400);
    
    return () => clearTimeout(timer);
  }, [article, isPremium, isPreviewMode, isProduction, isHomepage]);

  // Special case for homepage with a Vite reference
  if (isHomepage && article.content && isViteAssetReference(article.content)) {
    console.log("ArticleRenderer - Bypassing normal rendering for homepage with Vite reference");
    
    // For homepage, we create a modified article object with empty content
    const modifiedArticle = {
      ...article,
      content: "" // Replace the Vite reference with empty content for homepage
    };
    
    return (
      <Article
        title={modifiedArticle.title}
        author={modifiedArticle.author}
        publishDate={modifiedArticle.publishDate}
        category={modifiedArticle.category}
        readTime={modifiedArticle.readTime}
        initialReadCount={modifiedArticle.readCount}
        initialLikeCount={modifiedArticle.likeCount}
        content={modifiedArticle.content}
        externalUrl={modifiedArticle.externalUrl}
        previousArticleLink={previousArticleLink}
        previousArticleTitle={previousArticleTitle}
        isPremium={isPremium}
        isPreviewMode={isPreviewMode}
      />
    );
  }
  
  return (
    <Article
      title={article.title}
      author={article.author}
      publishDate={article.publishDate}
      category={article.category}
      readTime={article.readTime}
      initialReadCount={article.readCount}
      initialLikeCount={article.likeCount}
      content={article.content}
      externalUrl={article.externalUrl}
      previousArticleLink={previousArticleLink}
      previousArticleTitle={previousArticleTitle}
      isPremium={isPremium}
      isPreviewMode={isPreviewMode}
    />
  );
};

export default ArticleRenderer;
